import { useEffect, useState } from "react";
import { api } from "../api";
import { day, num, pct, rr, rupee, signClass, todayISO } from "../format";
import type { Journal, Rule, StrategyOption, Trade } from "../types";
import { PRODUCTS, RULE_CATEGORIES } from "../types";
import { LazyMount } from "./LazyMount";
import { Screenshot } from "./Screenshot";
import { TradeCharts } from "./TradeCharts";

interface Props {
  trade: Trade;
  rules: Rule[];
  strategies: StrategyOption[];
  onChange: (trade: Trade) => void;
  onDelete: (id: number) => void;
  /** Open with the rules, notes and fills already showing. */
  defaultExpanded?: boolean;
  /** Set false where the chart would be redundant or unwanted. */
  charts?: boolean;
  /** Parts the person has switched off, by CARD_PARTS key. */
  hidden?: string[];
}

/** One position: the stat strip, its fills, and everything you wrote about it. */
export function TradeCard({
  trade,
  rules,
  strategies,
  onChange,
  onDelete,
  defaultExpanded = false,
  charts = true,
  hidden = [],
}: Props) {
  const shows = (key: string) => !hidden.includes(key);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const journal = trade.journal;
  const isWin = trade.net_pnl !== null && trade.net_pnl > 0;
  const isClosed = trade.status === "closed";

  const save = async (patch: Partial<Journal>) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api.saveJournal(trade.id, patch));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="trade-head">
        <span className="trade-symbol">{trade.symbol}</span>
        {isClosed ? (
          <span className={`badge ${isWin ? "win" : "loss"}`}>{isWin ? "Win" : "Loss"}</span>
        ) : (
          <span className="badge open">Open</span>
        )}
        {trade.product === "mtf" && (
          <span className="badge mtf" title="Margin funded">
            MTF
          </span>
        )}
        {trade.source === "imported" && <span className="badge muted">Imported</span>}
        <div className="right row">
          <button className="ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Hide notes" : "Notes & rules"}
          </button>
          {trade.source === "manual" && (
            <button className="ghost danger" onClick={() => onDelete(trade.id)}>
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="stat-strip">
        {shows("avg_entry") && <Stat k="Avg Buying Price" v={num(trade.avg_entry)} />}
        {shows("avg_exit") && <Stat k="Avg Selling Price" v={num(trade.avg_exit)} />}
        {shows("qty") && <Stat k="Qty" v={String(trade.qty)} />}
        {shows("holding_days") && (
          <Stat k="Days Held" v={trade.holding_days ? String(trade.holding_days) : "—"} />
        )}
        {shows("pnl") && (
        <Stat
          k="P/L"
          v={
            trade.net_pnl === null
              ? "—"
              : `${pct(trade.pct_return)} (${rupee(trade.net_pnl)})`
          }
          cls={signClass(trade.net_pnl)}
        />
        )}
        {shows("portfolio_pnl") && (
        <Stat
          k="Portfolio P/L"
          v={pct(trade.portfolio_pnl_pct)}
          cls={signClass(trade.portfolio_pnl_pct)}
        />
        )}
        {shows("rr") && <Stat k="RR Ratio" v={rr(trade.rr_multiple)} />}
        {shows("risk") && (
        <Stat
          k="Risk ₹ | %"
          v={
            trade.risk_rupees === null
              ? "—"
              : `${num(trade.risk_rupees)} | ${num(trade.risk_pct)}`
          }
        />
        )}
      </div>

      {/* The stop is an input, not a readout: editing it re-derives risk and RR. */}
      {shows("journal_fields") && (
      <div className="grid cols-5" style={{ marginTop: 16 }}>
        <div>
          <label>SL Price</label>
          <StopInput value={journal?.sl_price ?? null} onCommit={(v) => save({ sl_price: v })} />
        </div>
        <div>
          <label>Strategy</label>
          <select
            value={journal?.strategy ?? ""}
            onChange={(e) => save({ strategy: (e.target.value || null) as never })}
          >
            <option value="">—</option>
            {strategies.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Funding</label>
          <select
            value={trade.product ?? "cash"}
            onChange={(e) => save({ product: e.target.value as never })}
          >
            {PRODUCTS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Setup Tag</label>
          <BlurInput
            value={journal?.setup_tag ?? ""}
            placeholder="e.g. flag breakout"
            onCommit={(v) => save({ setup_tag: v || null })}
          />
        </div>
        <div>
          <label>Conviction (1–5)</label>
          <select
            value={journal?.conviction ?? ""}
            onChange={(e) =>
              save({ conviction: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      )}

      {trade.anomaly && <div className="anomaly">⚠ {trade.anomaly}</div>}
      {error && <div className="banner error" style={{ marginTop: 12 }}>{error}</div>}

      {shows("excursion") && <Excursion trade={trade} />}

      {/* Always on, not behind the details toggle: the chart is how a trade is
          reviewed, and needing a click per card made scanning a closed list
          impractical. It mounts when scrolled near rather than on first paint. */}
      {charts && shows("charts") && (
        <LazyMount>
          <TradeCharts tradeId={trade.id} symbol={trade.symbol} isClosed={isClosed} />
        </LazyMount>
      )}

      {expanded && (
        <>
          <Legs trade={trade} onChange={onChange} onError={setError} />

          <div className="grid cols-2">
            {RULE_CATEGORIES.map((cat) => (
              <RuleGroup
                key={cat.key}
                label={cat.label}
                rules={rules.filter((r) => r.category === cat.key)}
                selected={journal?.rule_ids ?? []}
                disabled={busy}
                onToggle={(ids) => save({ rule_ids: ids })}
              />
            ))}
          </div>

          <Screenshot
            tradeId={trade.id}
            path={journal?.screenshot_path}
            onChange={() => save({})}
          />

          <div className="section-title">Notes</div>
          <div className="grid cols-2">
            <Note label="Thesis" value={journal?.thesis} onCommit={(v) => save({ thesis: v })} />
            <Note
              label="Exit Reason"
              value={journal?.exit_reason}
              onCommit={(v) => save({ exit_reason: v })}
            />
            <Note
              label="Mistakes"
              value={journal?.mistakes}
              onCommit={(v) => save({ mistakes: v })}
            />
            <Note
              label="Additional Notes"
              value={journal?.additional_notes}
              onCommit={(v) => save({ additional_notes: v })}
            />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * How far the trade went against you and in your favour while you held it.
 *
 * The point of showing it beside the realized return: a winner that first went
 * 8% against you is a different trade from one that never did, and only this
 * panel can tell them apart.
 */
function Excursion({ trade }: { trade: Trade }) {
  if (trade.mae === null || trade.mfe === null) return null;

  const perShare = (pctOf: number) => (pctOf / 100) * trade.avg_entry;
  const realizedPct =
    trade.avg_exit === null ? null : ((trade.avg_exit - trade.avg_entry) / trade.avg_entry) * 100;
  // What the trade offered versus what you kept. Blank when it never offered
  // anything, since dividing by zero favourable movement says nothing.
  const captured =
    realizedPct === null || trade.mfe === 0 ? null : (realizedPct / trade.mfe) * 100;

  return (
    <div className="excursion">
      <div className="grid cols-4">
        <Stat k="Entry Price" v={num(trade.avg_entry)} />
        <Stat k="Exit Price" v={trade.avg_exit === null ? "—" : num(trade.avg_exit)} />
        <Stat
          k="Lowest During Trade"
          v={trade.lowest_price === null ? "—" : num(trade.lowest_price)}
          cls="neg"
        />
        <Stat
          k="Highest During Trade"
          v={trade.highest_price === null ? "—" : num(trade.highest_price)}
          cls="pos"
        />
      </div>
      <div className="grid cols-4" style={{ marginTop: 12 }}>
        <Stat
          k="MAE — Worst Against You"
          v={`${pct(trade.mae, 1)} (${rupee(perShare(trade.mae))}/share)`}
          cls="neg"
        />
        <Stat
          k="MFE — Best In Your Favour"
          v={`${pct(trade.mfe, 1)} (${rupee(perShare(trade.mfe))}/share)`}
          cls="pos"
        />
        <Stat
          k="Realized Return"
          v={realizedPct === null ? "—" : `${pct(realizedPct, 1)} (${rupee(
            perShare(realizedPct),
          )}/share)`}
          cls={signClass(realizedPct)}
        />
        <Stat
          k="Captured Of MFE"
          v={captured === null ? "—" : pct(captured, 0)}
          cls={captured !== null && captured < 50 ? "warn" : undefined}
        />
      </div>
      <div className="hint">
        From daily highs and lows. The entry day's low may predate your fill, so MAE can
        read slightly worse than it was on day one.
      </div>
    </div>
  );
}

function Stat({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className={`v ${cls ?? ""}`}>{v}</div>
    </div>
  );
}

/** Commits on blur or Enter, so risk isn't recomputed on every keystroke. */
function StopInput({
  value,
  onCommit,
}: {
  value: number | null;
  onCommit: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);

  const commit = () => {
    const parsed = draft.trim() === "" ? null : Number(draft);
    if (parsed !== value && (parsed === null || !Number.isNaN(parsed))) onCommit(parsed);
  };

  return (
    <input
      type="number"
      step="0.05"
      value={draft}
      placeholder="—"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

function BlurInput({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== value && onCommit(draft)}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

function Note({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string | null | undefined;
  onCommit: (v: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  return (
    <div>
      <label>{label}</label>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== (value ?? "") && onCommit(draft || null)}
      />
    </div>
  );
}

function RuleGroup({
  label,
  rules,
  selected,
  disabled,
  onToggle,
}: {
  label: string;
  rules: Rule[];
  selected: number[];
  disabled: boolean;
  onToggle: (ids: number[]) => void;
}) {
  const chosen = rules.filter((r) => selected.includes(r.id)).length;
  return (
    <div>
      <div className="section-title">
        {label}{" "}
        <span className="muted">
          {rules.length === 0 ? "" : chosen === 0 ? "· none selected" : `· ${chosen} selected`}
        </span>
      </div>
      {rules.length === 0 ? (
        <div className="hint">No rules defined yet — add them in Settings.</div>
      ) : (
        <div className="rule-list">
          {rules.map((rule) => {
            const ticked = selected.includes(rule.id);
            return (
              <label key={rule.id} className={`rule ${ticked ? "" : "unticked"}`}>
                <input
                  type="checkbox"
                  checked={ticked}
                  disabled={disabled}
                  onChange={() =>
                    onToggle(
                      ticked ? selected.filter((id) => id !== rule.id) : [...selected, rule.id],
                    )
                  }
                />
                <span>{rule.text}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Legs({
  trade,
  onChange,
  onError,
}: {
  trade: Trade;
  onChange: (t: Trade) => void;
  onError: (m: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    kind: "sell",
    leg_date: todayISO(),
    quantity: "",
    price: "",
  });

  const manual = trade.source === "manual";

  const submit = async () => {
    onError(null);
    try {
      onChange(
        await api.addLeg(trade.id, {
          kind: form.kind,
          leg_date: form.leg_date,
          quantity: Number(form.quantity),
          price: Number(form.price),
        }),
      );
      setAdding(false);
      setForm({ kind: "sell", leg_date: todayISO(), quantity: "", price: "" });
    } catch (e) {
      onError((e as Error).message);
    }
  };

  return (
    <>
      <div className="section-title">
        Fills
        {manual && (
          <button
            className="ghost"
            style={{ float: "right", padding: "1px 8px" }}
            onClick={() => setAdding((v) => !v)}
          >
            {adding ? "Cancel" : "+ Add fill"}
          </button>
        )}
      </div>

      {trade.legs.length > 0 ? (
        <table className="legs">
          <thead>
            <tr>
              <th>Type</th>
              <th>Date</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {trade.legs.map((leg) => (
              <tr key={leg.id}>
                <td>
                  <span className={`badge ${leg.kind === "buy" ? "open" : "muted"}`}>
                    {leg.kind}
                  </span>
                </td>
                <td>{day(leg.leg_date)}</td>
                <td>{leg.quantity}</td>
                <td>{num(leg.price)}</td>
                <td>{rupee(leg.quantity * leg.price)}</td>
                <td>
                  {manual && trade.legs.length > 1 && (
                    <button
                      className="ghost danger"
                      style={{ padding: "1px 7px" }}
                      onClick={async () => {
                        onError(null);
                        try {
                          onChange(await api.deleteLeg(trade.id, leg.id));
                        } catch (e) {
                          onError((e as Error).message);
                        }
                      }}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="hint">
          Built from imported broker fills — edit the executions to change it.
        </div>
      )}

      {adding && (
        <div className="grid cols-4" style={{ marginTop: 12 }}>
          <div>
            <label>Type</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
            >
              <option value="sell">Sell (exit)</option>
              <option value="buy">Buy (add)</option>
            </select>
          </div>
          <div>
            <label>Date</label>
            <input
              type="date"
              value={form.leg_date}
              onChange={(e) => setForm({ ...form, leg_date: e.target.value })}
            />
          </div>
          <div>
            <label>Quantity</label>
            <input
              type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
          </div>
          <div>
            <label>Price</label>
            <div className="row">
              <input
                type="number"
                step="0.05"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
              <button
                className="primary"
                disabled={!form.quantity || !form.price}
                onClick={submit}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
